import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { BADGE_LABEL, PLAN_LABEL, PLAN_LIMITS, PLAN_PERKS, SELLER_PLANS, type PaidPlan, type SellerPlan } from "../../shared/market";
import { formatArs } from "../../shared/money";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi, type MySeller } from "../market/api";
import { PlanCompare } from "../market/PlanCompare";
import { takeUpgradeSource } from "../market/Showcase";
import { track, trackThen } from "../lib/analytics";

/** The few things that set each plan apart, for its card (the full list is the table below). */
function highlights(p: SellerPlan, t: (k: MessageKey, v?: Record<string, string | number>) => string): string[] {
  const l = PLAN_LIMITS[p];
  const k = PLAN_PERKS[p];
  const out = [l.listings === null ? t("plans.hlListingsNoLimit") : t("plans.hlListings", { count: l.listings }), l.maxPriceArs === null ? t("plans.hlPriceNoLimit") : t("plans.hlPrice", { price: formatArs(l.maxPriceArs) })];
  if (p === "basic") out.push(t("plans.hlNoFees"), t("plans.hlVerified"));
  if (k.featuredPerMonth) out.push(t("plans.hlFeatured", { count: k.featuredPerMonth }));
  if (k.insights) out.push(t("plans.hlInsights"));
  if (k.coupons) out.push(k.coupons === 1 ? t("plans.hlCoupon") : t("plans.hlCoupons", { count: k.coupons }));
  if (k.usdPrices) out.push(t("plans.hlUsd"));
  if (k.reservations) out.push(t("plans.hlReservations", { count: k.reservations }));
  if (k.customers) out.push(t("plans.hlCustomers"));
  if (k.shopEmailsPerMonth) out.push(t("plans.hlEmails", { count: k.shopEmailsPerMonth }));
  if (k.demandAlerts) out.push(t("plans.hlDemand"));
  if (k.customShop) out.push(t("plans.hlCustomShop"));
  if (k.api) out.push(t("plans.hlApi"));
  if (k.prioritySupport) out.push(t("plans.hlSupport"));
  return out;
}

/** /vender/planes — the three plans, what each includes, and subscribing to EX or VMAX through Mercado Pago. */
export function PlansPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const info = useAsync((signal) => marketApi.info(signal), []);
  const mine = useAsync((signal) => (user ? marketApi.mySeller(signal) : Promise.resolve(null)), [user?.id]);
  const [seller, setSeller] = useState<MySeller | null>(null);
  const shop = seller ?? mine.data ?? null;
  const on = Boolean(info.data?.planSubscriptions);
  const prices = info.data?.planPrices ?? null;
  const current = shop?.status === "active" ? (shop.plan?.name ?? "basic") : null;
  // No buttons until we know whether subscriptions are open and who's looking (the wrong one is a click away).
  const ready = Boolean(info.data) && (!user || !mine.loading || seller !== null);

  return (
    <div className="container section plans-page">
      <header className="plans-head">
        <h1>{t("plans.title")}</h1>
        <p className="muted">{t("plans.intro")}</p>
      </header>
      <div className="plans-grid">
        {SELLER_PLANS.map((p) => (
          <article key={p} className={`plan-tile plan-tile-${p}${current === p ? " is-current" : ""}`} aria-labelledby={`plan-${p}`}>
            <h2 id={`plan-${p}`}>
              {p !== "basic" && <span aria-hidden="true">{BADGE_LABEL[p].icon} </span>}
              {PLAN_LABEL[p]}
            </h2>
            <p className="plan-price">
              {p === "basic" ? t("plans.free") : prices ? t("plans.perMonth", { price: formatArs(prices[p]) }) : info.data ? t("plans.priceSoon") : " "}
            </p>
            <p className="muted small">{t(`plans.tagline.${p}` as MessageKey)}</p>
            <ul className="plan-highlights">
              {highlights(p, t).map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
            {ready && <PlanAction plan={p} shop={shop} signedIn={Boolean(user)} on={on} email={user?.email ?? ""} onSeller={setSeller} />}
          </article>
        ))}
      </div>

      <section className="section" aria-labelledby="plans-compare">
        <h2 id="plans-compare" className="section-title">
          {t("plans.compare")}
        </h2>
        <PlanCompare current={current ?? undefined} />
      </section>

      <section className="section plans-faq" aria-labelledby="plans-faq">
        <h2 id="plans-faq" className="section-title">
          {t("plans.faq")}
        </h2>
        {(["1", "2", "3", "4"] as const).map((n) => (
          <details key={n}>
            <summary>{t(`plans.q${n}` as MessageKey)}</summary>
            <p>{t(`plans.a${n}` as MessageKey)}</p>
          </details>
        ))}
      </section>
    </div>
  );
}

/** What a plan's card offers this visitor: open a shop, sign in, subscribe (or ask, before subscriptions open), or that it's theirs. */
function PlanAction({ plan, shop, signedIn, on, email, onSeller }: { plan: SellerPlan; shop: MySeller | null; signedIn: boolean; on: boolean; email: string; onSeller: (s: MySeller) => void }) {
  // Which "get the plan" button brought them here (?desde=, from UpgradeLink).
  const desde = useSearchParams()[0].get("desde");
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = shop?.status === "active";
  const currentPlan = active ? (shop.plan?.name ?? "basic") : null;
  const sub = shop?.subscription ?? null;
  const freeFounder = shop?.plan?.source === "founder" && !shop.plan.until && shop.plan.name !== "basic";

  if (plan === "basic") {
    if (!shop) return <Link to="/vender" className="btn btn-outline btn-block">{t("plans.openShop")}</Link>;
    return currentPlan === "basic" ? <p className="plan-note">{t("plans.yours")}</p> : null;
  }
  const paid = plan as PaidPlan;
  if (!signedIn) return <Link to={`/ingresar?next=${encodeURIComponent("/vender/planes")}`} className="btn btn-primary btn-block">{t("plans.signIn")}</Link>;
  if (!shop) return <Link to="/vender" className="btn btn-primary btn-block">{t("plans.openShopFirst")}</Link>;
  if (!active) return <p className="plan-note">{t("plans.underReview")}</p>;
  if (freeFounder) return <p className="plan-note">{currentPlan === paid ? t("plans.founderFree") : t("plans.founderHasVmax")}</p>;
  if (sub?.status === "authorized" && sub.plan === paid)
    return (
      <p className="plan-note">
        {t("plans.yours")} · <Link to="/mi-tienda/plan">{t("plans.manage")}</Link>
      </p>
    );

  if (!on) {
    // Before subscriptions open: "Quiero EX/VMAX" tells the owner, who writes with the price.
    const asked = shop.plan?.requested === paid && shop.plan.requestedAt !== null && Date.now() - new Date(shop.plan.requestedAt).getTime() < 86_400_000;
    if (currentPlan === paid) return <p className="plan-note">{t("plans.yours")}</p>;
    return asked ? (
      <p className="plan-note">{t("mkt.plan.requested", { plan: PLAN_LABEL[paid] })}</p>
    ) : (
      <button
        type="button"
        className="btn btn-outline btn-block"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const saved = await marketApi.requestPlan(paid);
            track("plan_request", { plan: paid, source: takeUpgradeSource(desde) });
            onSeller(saved);
          } finally {
            setBusy(false);
          }
        }}
      >
        {t("mkt.plan.ask", { plan: PLAN_LABEL[paid] })}
      </button>
    );
  }

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payerEmail = String(new FormData(e.currentTarget).get("payerEmail") ?? "").trim();
    setBusy(true);
    setError(null);
    try {
      const { url } = await marketApi.subscribe(paid, payerEmail);
      trackThen("plan_subscribe_start", { plan: paid, source: takeUpgradeSource(desde) }, () => window.location.assign(url));
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      setError(["founder_free", "already_subscribed", "subscriptions_off", "validation_error"].includes(code) ? t(`plans.err.${code}` as MessageKey) : t("plans.err.generic"));
      setBusy(false);
    }
  };
  const label = sub?.status === "authorized" ? t("plans.switchTo", { plan: PLAN_LABEL[paid] }) : t("plans.subscribe");
  return open ? (
    <form className="plan-subscribe stack-sm" onSubmit={submit}>
      <div className="field">
        <label htmlFor={`pe-${paid}`}>{t("plans.payerEmail")}</label>
        <input id={`pe-${paid}`} name="payerEmail" type="email" required defaultValue={email} autoComplete="email" />
        <span className="field-hint">{t("plans.payerEmailHint")}</span>
      </div>
      {sub?.status === "authorized" && <p className="small muted">{t("plans.switchNote", { plan: PLAN_LABEL[sub.plan] })}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
        {t("plans.goToMp")}
      </button>
      <button type="button" className="btn btn-link btn-sm" onClick={() => setOpen(false)}>
        {t("nav.close")}
      </button>
    </form>
  ) : (
    <button type="button" className={plan === "vmax" ? "btn btn-primary btn-block" : "btn btn-outline btn-block"} onClick={() => setOpen(true)}>
      {label}
    </button>
  );
}
